/**
 * Draws the VFX pool (story `q-demo-vfx-pass`): one `InstancedMesh` per layer
 * (`VFX_LAYERS`), each a unit quad with an additive `MeshBasicMaterial` over a small
 * PROCEDURAL texture (made here from maths — no file, no third-party asset). A layer with
 * no live particle is invisible, so an idle scene pays ZERO draw calls; a busy one at most
 * one per layer. Built-in materials only, so the same code runs on the WebGL2 default and
 * on the WebGPU path (WebGPURenderer converts built-ins to node materials) — no GLSL debt
 * (tgc-threejs-target §1). The per-frame `sync` writes the preallocated instance buffers
 * through scratch objects: no allocation per frame.
 */

import * as THREE from 'three';
import { VFX_LAYERS, type VfxLayer } from './presets.js';
import type { ParticlePool } from './pool.js';

/** Texture edge, px. 4 layers × 64² RGBA8 = 64 KiB (+⅓ for mips). */
export const VFX_TEXTURE_SIZE = 64;

/** Bytes of GPU texture memory the VFX layer holds (with mip chain). */
export function vfxTextureBytes(): number {
  return Math.round(VFX_LAYERS.length * VFX_TEXTURE_SIZE * VFX_TEXTURE_SIZE * 4 * (4 / 3));
}

/** Alpha in [0,1] of the layer's shape at normalised (u, v) ∈ [-1, 1]². Pure. */
export function layerAlpha(layer: VfxLayer, u: number, v: number): number {
  const r = Math.sqrt(u * u + v * v);
  switch (layer) {
    case 'glow': {
      const f = Math.max(0, 1 - r);
      return f * f;
    }
    case 'spark': {
      const f = Math.max(0, 1 - r);
      return Math.min(1, f * f * f * 2.2);
    }
    case 'star': {
      // a four-point sparkle: two thin crossed beams + a soft core
      const beam = (a: number, b: number): number =>
        Math.max(0, 1 - Math.abs(a) * 9) * Math.max(0, 1 - Math.abs(b));
      const core = Math.max(0, 1 - r * 2.5);
      return Math.min(1, beam(u, v) + beam(v, u) + core * core);
    }
    case 'ring': {
      const band = Math.max(0, 1 - Math.abs(r - 0.82) / 0.13);
      const inner = r < 0.82 ? Math.max(0, 1 - (0.82 - r) / 0.5) * 0.25 : 0;
      return r > 1 ? 0 : Math.min(1, band * band + inner);
    }
  }
}

function makeTexture(layer: VfxLayer): THREE.DataTexture {
  const n = VFX_TEXTURE_SIZE;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      const u = ((x + 0.5) / n) * 2 - 1;
      const v = ((y + 0.5) / n) * 2 - 1;
      const a = layerAlpha(layer, u, v);
      const o = (y * n + x) * 4;
      // Additive: colour carries the shape (premultiplied white × alpha).
      const c = Math.round(a * 255);
      data[o] = c;
      data[o + 1] = c;
      data[o + 2] = c;
      data[o + 3] = c;
    }
  }
  const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export interface VfxRendererStats {
  drawCalls: number;
  instances: number;
  /** Rough screen coverage of all live quads ÷ screen area (an overdraw estimate). */
  coverage: number;
}

export class VfxRenderer {
  readonly group = new THREE.Group();
  private readonly meshes: THREE.InstancedMesh[] = [];
  private readonly textures: THREE.DataTexture[] = [];
  private readonly geometry = new THREE.PlaneGeometry(1, 1);
  private readonly scratchM = new THREE.Matrix4();
  private readonly scratchP = new THREE.Vector3();
  private readonly scratchS = new THREE.Vector3();
  private readonly scratchC = new THREE.Color();
  private readonly flatQ = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(1, 0, 0),
    -Math.PI / 2,
  );
  private readonly writeAt: Uint16Array;
  readonly stats: VfxRendererStats = { drawCalls: 0, instances: 0, coverage: 0 };

  constructor(capacityPerLayer: number) {
    this.group.name = 'vfx';
    // Capture tooling: short-lived feedback, like telegraphs (Game.ts).
    this.group.userData.transient = 'vfx';
    this.writeAt = new Uint16Array(VFX_LAYERS.length);
    for (const layer of VFX_LAYERS) {
      const tex = makeTexture(layer);
      this.textures.push(tex);
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        fog: false,
        side: THREE.DoubleSide,
        // one draw per layer: a transparent DoubleSide material otherwise renders back then front
        // faces as TWO draws (measured: +2 draws and 2x triangles per busy layer, run 124844Z)
        forceSinglePass: true,
      });
      const mesh = new THREE.InstancedMesh(this.geometry, mat, capacityPerLayer);
      mesh.name = `vfx-${layer}`;
      mesh.frustumCulled = false; // the quads move; the geometry's bound means nothing
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = layer === 'ring' ? 5 : 10;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // allocate instanceColor up front (setColorAt would lazily allocate on first use)
      mesh.instanceColor = new THREE.InstancedBufferAttribute(
        new Float32Array(capacityPerLayer * 3),
        3,
      );
      mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
      mesh.count = 0;
      mesh.visible = false;
      mesh.userData.transient = 'vfx';
      this.meshes.push(mesh);
      this.group.add(mesh);
    }
  }

  /** Copy the pool into the instance buffers, facing `camera`. Allocation-free. */
  sync(
    pool: ParticlePool,
    camera: THREE.Camera,
    viewportWidthPx: number,
    viewportHeightPx: number,
  ): void {
    this.writeAt.fill(0);
    const cap = this.meshes[0]?.instanceMatrix.count ?? 0;
    const camQ = camera.quaternion;
    const camPos = camera.position;
    const persp = camera as THREE.PerspectiveCamera;
    const tanHalf = Math.tan(((persp.fov ?? 50) * Math.PI) / 360);
    let coveragePx2 = 0;
    for (let i = 0; i < pool.count; i += 1) {
      const li = pool.layer[i]!;
      const slot = this.writeAt[li]!;
      if (slot >= cap) continue;
      this.writeAt[li] = slot + 1;
      const t = pool.t(i);
      // A shrinking particle eases out (holds its size, then goes); a growing one is linear.
      const s0 = pool.size0[i]!;
      const s1 = pool.size1[i]!;
      const size = s0 + (s1 - s0) * (s1 < s0 ? t * t : t);
      const fade = li === 0 /* ring */ || li === 1 /* glow */ ? 1 - t : 1 - t * t;
      this.scratchP.set(pool.px[i]!, pool.py[i]!, pool.pz[i]!);
      this.scratchS.set(size, size, size);
      this.scratchM.compose(this.scratchP, li === 0 ? this.flatQ : camQ, this.scratchS);
      const mesh = this.meshes[li]!;
      mesh.setMatrixAt(slot, this.scratchM);
      this.scratchC.setRGB(
        (pool.r0[i]! + (pool.r1[i]! - pool.r0[i]!) * t) * fade,
        (pool.g0[i]! + (pool.g1[i]! - pool.g0[i]!) * t) * fade,
        (pool.b0[i]! + (pool.b1[i]! - pool.b0[i]!) * t) * fade,
        THREE.LinearSRGBColorSpace,
      );
      mesh.setColorAt(slot, this.scratchC);
      // overdraw estimate: the quad's projected pixel edge², squared
      const d = Math.max(0.5, this.scratchP.distanceTo(camPos));
      const px = (size / (2 * d * tanHalf)) * viewportHeightPx;
      coveragePx2 += px * px;
    }
    let draws = 0;
    let instances = 0;
    for (let li = 0; li < this.meshes.length; li += 1) {
      const mesh = this.meshes[li]!;
      const n = this.writeAt[li]!;
      mesh.count = n;
      mesh.visible = n > 0;
      if (n > 0) {
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        draws += 1;
        instances += n;
      }
    }
    this.stats.drawCalls = draws;
    this.stats.instances = instances;
    this.stats.coverage = coveragePx2 / Math.max(1, viewportWidthPx * viewportHeightPx);
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const m of this.meshes) {
      (m.material as THREE.Material).dispose();
      m.dispose();
    }
    for (const t of this.textures) t.dispose();
    this.geometry.dispose();
  }
}
